import { produce } from 'immer';
import { ErrorLevel } from 'app/lib/constants';
import type { TypedReduxActionOutcomes } from 'app/store/storeTypes';
import {
    type GetSnowflakeConfigurationFailure,
    type GetSnowflakeConfigurationSuccess,
    SNOWFLAKE_CONNECTION,
    SNOWFLAKE_MULTI_USER_CONFIGURATION,
    SNOWFLAKE_O_AUTH2_CONNECTION,
    type SetSnowflakeMultiUserConfigurationFailure,
    type SetSnowflakeOAuth2ConnectionAdvancedFailure,
    type SetSnowflakeOAuth2ConnectionDiscoveryFailure,
    type SetSnowflakeServiceAccountConfigurationFailure,
    type SetSnowflakeUserCredentialsFailure,
    SnowflakeConfigurationType
} from 'app/lib/types/server';
import { extractError } from 'app/lib/error';
import {
    GET_SNOWFLAKE_CONFIGURATION_ACTION_TYPE,
    SET_SNOWFLAKE_SERVICE_ACCOUNT_CONFIGURATION_ACTION_TYPE,
    SET_SNOWFLAKE_USER_CREDENTIALS_ACTION_TYPE,
    TOGGLE_CONNECTION_STATUS_DIALOG
} from './types';

export type SnowflakeConfigurationSlice = {
    getConfiguration: {
        type: null | SnowflakeConfigurationType;
        properties: null | GetSnowflakeConfigurationSuccess;
        error: null | GetSnowflakeConfigurationFailure;
        isLoading: boolean;
    };
    setConfiguration: {
        type: null | SnowflakeConfigurationType;
        error:
            | null
            | SetSnowflakeMultiUserConfigurationFailure
            | SetSnowflakeOAuth2ConnectionDiscoveryFailure
            | SetSnowflakeOAuth2ConnectionAdvancedFailure
            | SetSnowflakeServiceAccountConfigurationFailure;
        isLoading: boolean;
    };
    setUserCredentials: {
        isLoading: boolean;
        error: null | SetSnowflakeUserCredentialsFailure;
    };
    isConnectionStatusDialogOpen: boolean;
};

export const INITIAL_STATE: SnowflakeConfigurationSlice = {
    getConfiguration: {
        type: null,
        properties: null,
        error: null,
        isLoading: false
    },
    setConfiguration: {
        type: null,
        isLoading: false,
        error: null
    },
    setUserCredentials: {
        isLoading: false,
        error: null
    },
    isConnectionStatusDialogOpen: false
};

export const snowflakeConfigurationReducer = produce((draft, action: TypedReduxActionOutcomes) => {
    switch (action.type) {
        case GET_SNOWFLAKE_CONFIGURATION_ACTION_TYPE.STARTED:
            draft.getConfiguration.isLoading = true;
            draft.getConfiguration.error = null;
            break;
        case GET_SNOWFLAKE_CONFIGURATION_ACTION_TYPE.SUCCEEDED: {
            const { _kind } = action.payload;
            let type;

            switch (_kind) {
                case SNOWFLAKE_CONNECTION:
                    type = SnowflakeConfigurationType.SERVICE_ACCOUNT;
                    break;

                case SNOWFLAKE_MULTI_USER_CONFIGURATION:
                    type = SnowflakeConfigurationType.MULTI_USER_USERNAME_PASSWORD;
                    break;

                case SNOWFLAKE_O_AUTH2_CONNECTION:
                    type = SnowflakeConfigurationType.EXTERNAL_OAUTH;
                    break;

                default:
                    type = null;
                    break;
            }

            draft.getConfiguration.type = type;
            draft.getConfiguration.properties = action.payload;

            break;
        }
        case GET_SNOWFLAKE_CONFIGURATION_ACTION_TYPE.FAILED:
            draft.getConfiguration.error = extractError(action.payload);
            break;
        case GET_SNOWFLAKE_CONFIGURATION_ACTION_TYPE.ENDED:
            draft.getConfiguration.isLoading = false;
            break;

        case SET_SNOWFLAKE_SERVICE_ACCOUNT_CONFIGURATION_ACTION_TYPE.STARTED:
            draft.setConfiguration.error = null;
            draft.setConfiguration.type = null;
            draft.setConfiguration.isLoading = true;
            break;
        case SET_SNOWFLAKE_SERVICE_ACCOUNT_CONFIGURATION_ACTION_TYPE.FAILED:
            draft.setConfiguration.error = extractError(action.payload, ErrorLevel.ERROR);
            draft.setConfiguration.type = SnowflakeConfigurationType.SERVICE_ACCOUNT;
            break;
        case SET_SNOWFLAKE_SERVICE_ACCOUNT_CONFIGURATION_ACTION_TYPE.ENDED:
            draft.setConfiguration.isLoading = false;
            break;

        case SET_SNOWFLAKE_USER_CREDENTIALS_ACTION_TYPE.STARTED:
            draft.setUserCredentials.error = null;
            draft.setUserCredentials.isLoading = true;
            break;
        case SET_SNOWFLAKE_USER_CREDENTIALS_ACTION_TYPE.FAILED:
            draft.setUserCredentials.error = extractError(action.payload, ErrorLevel.ERROR);
            break;
        case SET_SNOWFLAKE_USER_CREDENTIALS_ACTION_TYPE.ENDED:
            draft.setUserCredentials.isLoading = false;
            break;
        case TOGGLE_CONNECTION_STATUS_DIALOG: {
            draft.isConnectionStatusDialogOpen = action.payload.isOpen;
            break;
        }
        default:
            break;
    }
}, INITIAL_STATE);
